import {
  Box,
  Chip,
  Grid,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { Close, Search as SearchIcon, Star, StarBorder } from '@material-ui/icons';
import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { PROGRAM_MANAGEMENT_GET_ACTIVE_VOLUNTEERS } from '../../../../admin/program-management/program-management.gql';
import {
  ProgramManagementGetActiveVolunteers,
  ProgramManagementGetActiveVolunteersVariables,
} from '../../../../admin/program-management/__generated__/ProgramManagementGetActiveVolunteers';
import { Avatar } from '../../../../general/user-profile/components/Avatar';
import { ActivityFormValues } from '../ActivityFormV2';
import { AlertCard, getAlertCardColors } from '../../../../../common/cards/alert-card/AlertCard';

type InvitedVolunteer = ActivityFormValues['invitedVolunteers'][number];

export const TeamStep = () => {
  const { values, setFieldValue } = useFormikContext<ActivityFormValues>();
  const { theme } = useCampfireTheme();
  const { darkTextColor } = getAlertCardColors('info');
  const [searchText, setSearchText] = useState('');

  const { data, loading } = useCampfireQuery<
    ProgramManagementGetActiveVolunteers,
    ProgramManagementGetActiveVolunteersVariables
  >(PROGRAM_MANAGEMENT_GET_ACTIVE_VOLUNTEERS, {
    options: {
      variables: { programId: values.programId },
      skip: !values.programId,
    },
  });

  const allVolunteers = useMemo(() => data?.vm?.program?.activeVolunteers ?? [], [data]);

  // Clear invited volunteers if program changes
  const prevProgramId = useRef(values.programId);
  useEffect(() => {
    if (prevProgramId.current && prevProgramId.current !== values.programId && values.invitedVolunteers.length > 0) {
      setFieldValue('invitedVolunteers', []);
    }
    prevProgramId.current = values.programId;
  }, [values.programId]);

  const addedIds = new Set(values.invitedVolunteers.map((v) => v.volunteerId));

  const filteredVolunteers = useMemo(() => {
    if (!searchText.trim()) return allVolunteers;
    const lower = searchText.toLowerCase();
    return allVolunteers.filter(
      (v) => v.profile.preferredName.toLowerCase().includes(lower) || v.profile.lastName.toLowerCase().includes(lower)
    );
  }, [allVolunteers, searchText]);

  const handleAdd = (vol: {
    volunteerId: string;
    profile: { preferredName: string; lastName: string; avatarUrl: string | null };
  }) => {
    if (addedIds.has(vol.volunteerId)) return;
    const updated: InvitedVolunteer[] = [
      ...values.invitedVolunteers,
      {
        volunteerId: vol.volunteerId,
        preferredName: vol.profile.preferredName,
        lastName: vol.profile.lastName,
        avatarUrl: vol.profile.avatarUrl,
        isActivityLeader: false,
      },
    ];
    setFieldValue('invitedVolunteers', updated);
  };

  const handleRemove = (volunteerId: string) => {
    setFieldValue(
      'invitedVolunteers',
      values.invitedVolunteers.filter((v) => v.volunteerId !== volunteerId)
    );
  };

  const handleToggleAL = (volunteerId: string) => {
    setFieldValue(
      'invitedVolunteers',
      values.invitedVolunteers.map((v) =>
        v.volunteerId === volunteerId ? { ...v, isActivityLeader: !v.isActivityLeader } : v
      )
    );
  };

  return (
    <Box>
      <Typography style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Add Team</Typography>
      <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
        Add volunteers from this program to the activity team. You can also do this later from the activity management
        screen.
      </Typography>

      {values.invitedVolunteers.length > 0 && (
        <Box mb={2}>
          <AlertCard variant='info' title='Enrolment notifications'>
            <Typography style={{ fontSize: 14, fontWeight: 400, color: darkTextColor }}>
              {values.invitedVolunteers.length} volunteer{values.invitedVolunteers.length !== 1 ? 's' : ''} will be
              enrolled and receive an enrolment notification email when this activity is created.
            </Typography>
          </AlertCard>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Box
            border={`1px solid ${theme.color.grey.border}`}
            borderRadius={8}
            style={{ minHeight: 400, display: 'flex', flexDirection: 'column' }}
          >
            <Box px={2} py={1.5} borderBottom={`1px solid ${theme.color.grey.border}`}>
              <Typography style={{ fontWeight: 600, fontSize: 14 }}>
                Program volunteers ({allVolunteers.length})
              </Typography>
            </Box>

            <Box px={2} py={1} borderBottom={`1px solid ${theme.color.grey.border}`} display='flex' alignItems='center'>
              <SearchIcon style={{ color: '#aaa', fontSize: 20, marginRight: 8 }} />
              <InputBase
                placeholder='Filter by name...'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                fullWidth
                style={{ fontSize: 14 }}
              />
            </Box>

            <Box flex={1} overflow='auto' style={{ maxHeight: 320 }}>
              {loading ? (
                <Box p={3} textAlign='center'>
                  <Typography variant='body2' color='textSecondary'>
                    Loading volunteers...
                  </Typography>
                </Box>
              ) : filteredVolunteers.length === 0 ? (
                <Box p={3} textAlign='center'>
                  <Typography variant='body2' color='textSecondary'>
                    {searchText ? 'No matching volunteers' : 'No volunteers in this program'}
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {filteredVolunteers.map((vol) => {
                    const alreadyAdded = addedIds.has(vol.volunteerId);
                    return (
                      <ListItem
                        key={vol.volunteerId}
                        button={!alreadyAdded as any}
                        onClick={() => !alreadyAdded && handleAdd(vol)}
                        style={alreadyAdded ? { opacity: 0.4 } : undefined}
                      >
                        <ListItemAvatar>
                          <Avatar
                            preferredName={vol.profile.preferredName}
                            lastName={vol.profile.lastName}
                            avatarUrl={vol.profile.avatarUrl}
                            size={32}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography style={{ fontSize: 14 }}>
                              {vol.profile.preferredName} {vol.profile.lastName}
                            </Typography>
                          }
                        />
                        {alreadyAdded && <Chip label='Added' size='small' style={{ fontSize: 11 }} />}
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            border={`1px solid ${theme.color.grey.border}`}
            borderRadius={8}
            style={{ minHeight: 400, display: 'flex', flexDirection: 'column' }}
          >
            <Box
              px={2}
              py={1.5}
              borderBottom={`1px solid ${theme.color.grey.border}`}
              display='flex'
              justifyContent='space-between'
              alignItems='center'
            >
              <Typography style={{ fontWeight: 600, fontSize: 14 }}>
                Team ({values.invitedVolunteers.length})
              </Typography>
              <Box display='flex' alignItems='center'>
                <Star style={{ fontSize: 14, color: '#F5A623', marginRight: 4 }} />
                <Typography style={{ fontSize: 11, color: '#808080' }}>= Activity Leader</Typography>
              </Box>
            </Box>

            <Box flex={1} overflow='auto' style={{ maxHeight: 360 }}>
              {values.invitedVolunteers.length === 0 ? (
                <Box p={3} textAlign='center'>
                  <Typography variant='body2' color='textSecondary'>
                    No volunteers added yet
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {values.invitedVolunteers.map((vol) => (
                    <ListItem key={vol.volunteerId}>
                      <ListItemAvatar>
                        <Avatar
                          preferredName={vol.preferredName}
                          lastName={vol.lastName}
                          avatarUrl={vol.avatarUrl}
                          size={32}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography style={{ fontSize: 14 }}>
                            {vol.preferredName} {vol.lastName}
                          </Typography>
                        }
                        secondary={
                          vol.isActivityLeader ? (
                            <Typography style={{ fontSize: 11, fontWeight: 600, color: '#F5A623' }}>
                              Activity Leader
                            </Typography>
                          ) : null
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size='small'
                          onClick={() => handleToggleAL(vol.volunteerId)}
                          title={vol.isActivityLeader ? 'Remove Activity Leader' : 'Promote to Activity Leader'}
                        >
                          {vol.isActivityLeader ? (
                            <Star style={{ fontSize: 18, color: '#F5A623' }} />
                          ) : (
                            <StarBorder style={{ fontSize: 18, color: '#ccc' }} />
                          )}
                        </IconButton>
                        <IconButton size='small' onClick={() => handleRemove(vol.volunteerId)}>
                          <Close style={{ fontSize: 16 }} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
